import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type Session } from '@/lib/api';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useSessionPlayer } from '@/hooks/use-session-player';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { JoinSkeleton } from '@/components/game/join-skeleton';

const INTENSITY_LABELS: Record<number, string> = {
  1: 'Soft - on rigole',
  2: 'Curieux - on apprend à se connaître',
  3: 'Intime - on se livre',
  4: 'Chaud - on parle attirance',
  5: 'Hot - sans tabou',
};

export function JoinPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const [pseudo, setPseudo] = useLocalStorage('ember.pseudo');
  const { playerId, setPlayerId } = useSessionPlayer(shortCode);
  const [session, setSession] = useState<Session | null>(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [intensity, setIntensity] = useState(3);
  const [optIns, setOptIns] = useState({ sex: false, emotion: true, dare: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shortCode) return;
    api
      .getSession(shortCode)
      .then((s) => {
        setSession(s);
        // Si on a deja un playerId stocke pour cette session ET qu'il correspond
        // a un joueur actif, on bypass l'onboarding et on file direct au salon.
        // Permet de gerer le cas reload de page ou retour sur le lien apres avoir
        // deja rejoint sans creer de doublon.
        if (playerId && s.players.some((p) => p.id === playerId)) {
          navigate(`/play/${shortCode}`, { replace: true });
        }
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Erreur réseau'))
      .finally(() => setLoadingSession(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortCode]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!shortCode) return;
    if (!pseudo.trim()) {
      setError('Choisis un pseudo');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.joinSession(shortCode, {
        pseudo: pseudo.trim(),
        intensity,
        optIns,
      });
      setPlayerId(res.playerId);
      navigate(`/play/${shortCode}`);
    } catch (err) {
      if (err instanceof ApiError) {
        // Message specifique pour la collision de pseudo
        if (err.code === 'PSEUDO_TAKEN') {
          setError(
            `Le pseudo "${pseudo.trim()}" est déjà pris dans cette partie. Choisis-en un autre.`,
          );
        } else {
          setError(err.message);
        }
      } else {
        setError('Erreur réseau');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingSession) {
    return <JoinSkeleton />;
  }
  if (!session) {
    return (
      <div className="max-w-md mx-auto text-center space-y-4">
        <h1 className="text-2xl font-bold">Session introuvable</h1>
        <p className="text-muted-foreground">Le code {shortCode} ne correspond à aucune partie.</p>
        <Button className="w-full sm:w-auto min-h-[44px]" onClick={() => navigate('/')}>
          Retour
        </Button>
      </div>
    );
  }

  const isFirstPlayer = session.players.length === 0;
  const otherPseudo = session.players[0]?.pseudo;

  return (
    <form onSubmit={handleSubmit} className="max-w-lg mx-auto space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">
          {isFirstPlayer ? 'Configure ta partie' : `Tu rejoins ${otherPseudo}`}
        </h1>
        <p className="text-muted-foreground">
          Code : <span className="font-mono text-primary">{session.shortCode}</span>
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ton pseudo</CardTitle>
        </CardHeader>
        <CardContent>
          <Label htmlFor="join-pseudo" className="sr-only">
            Ton pseudo
          </Label>
          <Input
            id="join-pseudo"
            value={pseudo}
            onChange={(e) => setPseudo(e.target.value)}
            placeholder="Ex: Sam"
            maxLength={30}
            autoFocus
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Niveau d'intensité</CardTitle>
          <CardDescription>
            Tu peux baisser, l'autre joueur aussi. L'app prend toujours le minimum des deux (consent
            by design).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Slider
            value={[intensity]}
            onValueChange={(v) => v[0] !== undefined && setIntensity(v[0])}
            min={1}
            max={5}
            step={1}
            aria-label="Niveau d'intensité de la partie"
          />
          <p className="text-center text-sm">
            <span className="text-primary font-semibold">Niveau {intensity}</span>
            <span className="text-muted-foreground"> - {INTENSITY_LABELS[intensity]}</span>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sujets autorisés</CardTitle>
          <CardDescription>
            Coche ce qui te va. L'app ne tirera que des questions où tous les joueurs sont OK.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <OptInRow
            id="opt-sex"
            label="Parler de sexe me va"
            checked={optIns.sex}
            onChange={(v) => setOptIns({ ...optIns, sex: v })}
          />
          <OptInRow
            id="opt-emotion"
            label="Parler d'émotions / passé amoureux me va"
            checked={optIns.emotion}
            onChange={(v) => setOptIns({ ...optIns, emotion: v })}
          />
          <OptInRow
            id="opt-dare"
            label="Défis physiques (envoyer photo/audio) me va"
            checked={optIns.dare}
            onChange={(v) => setOptIns({ ...optIns, dare: v })}
          />
        </CardContent>
      </Card>

      <Button type="submit" size="lg" className="w-full min-h-[44px]" disabled={submitting}>
        {submitting ? 'Connexion...' : 'Rejoindre la partie'}
      </Button>

      {error && (
        <p className="text-sm text-destructive text-center" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

function OptInRow({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center space-x-3 py-1">
      <Checkbox id={id} checked={checked} onCheckedChange={(state) => onChange(state === true)} />
      <Label htmlFor={id} className="cursor-pointer min-h-[44px] flex items-center flex-1">
        {label}
      </Label>
    </div>
  );
}
